import { z } from 'zod';
import { motorNaoTratado, type Motor } from '@remoto/protocolo';

/**
 * Todos os modos do Claude Code ficam disponíveis (decisão D16), mas os dois
 * que dispensam aprovação exigem papel ADMIN e confirmação na interface.
 */
export const MODOS_PERMISSAO = [
  { id: 'default', rotulo: 'Perguntar sempre', perigoso: false },
  { id: 'acceptEdits', rotulo: 'Aceitar edições', perigoso: false },
  { id: 'plan', rotulo: 'Planejar antes', perigoso: false },
  { id: 'auto', rotulo: 'Automático', perigoso: false },
  { id: 'dontAsk', rotulo: 'Não perguntar', perigoso: true },
  { id: 'bypassPermissions', rotulo: 'Ignorar permissões', perigoso: true },
] as const;

export type IdModoPermissao = (typeof MODOS_PERMISSAO)[number]['id'];

export const ModoPermissaoSchema = z.enum(
  MODOS_PERMISSAO.map((m) => m.id) as [IdModoPermissao, ...IdModoPermissao[]],
);

export const MODO_PADRAO: IdModoPermissao = 'acceptEdits';

export function modoPerigoso(modo: IdModoPermissao): boolean {
  return MODOS_PERMISSAO.find((m) => m.id === modo)?.perigoso ?? false;
}

/**
 * Os dois modos que NÃO existem no Codex, e por que cada um.
 *
 * `plan`: o Codex não tem `ExitPlanMode`. O "plan" dele é a lista de tarefas do
 * próprio agente, que não espera decisão de ninguém — então o modo nunca
 * produziria o cartão que o destrava, e a sessão nasceria travada sem como
 * destravar pelo painel.
 *
 * `dontAsk`: "não pergunte, e negue o que não estiver pré-aprovado" emperra a
 * sessão sem dar como destravar. No Claude ele existe por herança; no motor
 * novo não nasce.
 */
const FORA_DO_CODEX: readonly IdModoPermissao[] = ['plan', 'dontAsk'];

/**
 * Os dois modos que NÃO existem no OpenCode (D15), e por que cada um.
 *
 * `auto`: é um CLASSIFICADOR do Claude Code, que aprova, nega e escala sozinho.
 * O OpenCode não tem nada parecido — "auto" ali seria um nome sem mecanismo.
 *
 * `dontAsk`: a mesma razão do Codex — emperra a sessão sem dar como destravar.
 *
 * `plan` EXISTE no OpenCode, ao contrário do Codex: é o agente `plan` nativo,
 * só leitura, e é por isso que ele fica na lista.
 */
const FORA_DO_OPENCODE: readonly IdModoPermissao[] = ['auto', 'dontAsk'];

/**
 * Os modos que NÃO existem em cada motor. `switch` exaustivo: o `motor !==
 * 'CODEX'` que existia aqui dava ao terceiro motor os seis modos do Claude,
 * inclusive `auto`, sem erro nenhum.
 */
function foraDoMotor(motor: Motor): readonly IdModoPermissao[] {
  switch (motor) {
    case 'CLAUDE':
      return [];
    case 'CODEX':
      return FORA_DO_CODEX;
    case 'OPENCODE':
      return FORA_DO_OPENCODE;
    default:
      return motorNaoTratado(motor);
  }
}

/** Os modos que a tela pode oferecer para este motor. */
export function modosDoMotor(motor: Motor): IdModoPermissao[] {
  const fora = foraDoMotor(motor);
  return MODOS_PERMISSAO.map((m) => m.id).filter((id) => !fora.includes(id));
}

export function modoDisponivelNoMotor(modo: IdModoPermissao, motor: Motor): boolean {
  return !foraDoMotor(motor).includes(modo);
}

/** O papel da MÁQUINA (a coluna `Maquina.papel`), nunca o do usuário. */
export type PapelDaMaquina = 'PC' | 'VPS';

/**
 * O modo em que a MESTRE de cada motor nasce.
 *
 * Claude e Codex nascem em `auto` e podem trocar depois (D50: `auto` e
 * `bypassPermissions`). A do OpenCode nasce em `bypassPermissions` e NÃO troca
 * (D3, Interface congelada item 2): a trava dela é a deny-list da camada
 * gerenciada, e o modo é fixo — a troca é recusada com
 * `modo_fixo_da_mestre_opencode`.
 *
 * **Não passa por `podeUsarModo`, e é decisão.** O modo da mestre não é
 * escolha de quem cria: é a forma da mestre daquele motor. Passá-lo pelo portão
 * de modo da sessão comum daria `modo_indisponivel_na_vps` à mestre do OpenCode
 * (que MORA na VPS, D1) — e a saída "fácil" seria afrouxar o portão da sessão
 * comum, que é exatamente o que ele existe para não deixar.
 */
export function modoInicialDaMestre(motor: Motor): IdModoPermissao {
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX':
      return 'auto';
    case 'OPENCODE':
      return 'bypassPermissions';
    default:
      return motorNaoTratado(motor);
  }
}

/** Se a mestre deste motor pode trocar de modo depois de nascer. */
export function modoDaMestreEhFixo(motor: Motor): boolean {
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX':
      return false;
    case 'OPENCODE':
      return true;
    default:
      return motorNaoTratado(motor);
  }
}

/**
 * Se este motor pode ter uma sessão desta FUNÇÃO nesta máquina (D1).
 *
 * O OpenCode roda nos PCs com sessões de trabalho, e na VPS **só a mestre**: um
 * processo ocioso come ~400 MB (OC-15), a RAM da VPS é do vizinho, e lá não há
 * sandbox por baixo (OC-9). Sessão comum OPENCODE na VPS é recusada aqui, no
 * boot do daemon e na API — três lugares, a mesma função.
 *
 * Claude e Codex: a recusa por máquina deles é por MODO (`modoDisponivelNaMaquina`)
 * e por CAPACIDADE (a sonda), não por função — por isso `ok` aqui.
 *
 * Aceita o papel nas duas grafias (`VPS` da coluna, `vps` do `PAPEL` do
 * daemon) porque os dois lados a chamam, e uma conversão esquecida num deles
 * faria `'vps' !== 'VPS'` liberar a sessão calada.
 */
export function motorDisponivelNaMaquina(
  motor: Motor,
  papelDaMaquina: PapelDaMaquina | 'pc' | 'vps',
  funcao: 'NORMAL' | 'MESTRE',
): { ok: true } | { ok: false; codigo: 'opencode_so_mestre_na_vps' } {
  const naVps = papelDaMaquina.toUpperCase() === 'VPS';
  switch (motor) {
    case 'CLAUDE':
    case 'CODEX':
      return { ok: true };
    case 'OPENCODE':
      if (naVps && funcao !== 'MESTRE') return { ok: false, codigo: 'opencode_so_mestre_na_vps' };
      return { ok: true };
    default:
      return motorNaoTratado(motor);
  }
}

/**
 * O Codex na VPS **não sai do sandbox**, e é justamente por isso que ele pode
 * morar lá (D6 reaberta pelo dono em 2026-09-17).
 *
 * O Claude na VPS é segurado por três camadas: as managed settings do Claude
 * Code, o hook `PreToolUse` do daemon (`guardRailBarra`) e o sudoers. **Nenhuma
 * das duas primeiras existe no caminho do Codex**: lá não há hook, e — medido
 * na M0-8 — dentro do sandbox de escrita comando e edição **não pedem
 * aprovação nenhuma**, então não há gesto para interceptar. O que sobra é o
 * sandbox em si: sem rede, sem `sudo` (o `no_new_privs` do bubblewrap derruba
 * o setuid), escrita só no `cwd` e no `/tmp`.
 *
 * `bypassPermissions` mapeia para `danger-full-access`, que **desliga o
 * sandbox**. Numa máquina que roda produção de terceiros isso não é um modo
 * mais solto: é a única barreira do motor, retirada. Na VPS ele não existe.
 *
 * A quarta camada é do próprio Codex e mora fora daqui
 * (`infra/vps/codex/requirements.toml`, `allowed_sandbox_modes`): com ela, o
 * app-server recusa `danger-full-access` mesmo que este código erre.
 *
 * **`bypassTotal` (ADR-0023, F4 da revisão de out/2026).** Com a chave
 * `BYPASS_TOTAL` ligada NOS DOIS LADOS (a API e o daemon daquela máquina), o
 * bypass do Codex existe na VPS — e lá ele NÃO é `danger-full-access`: é
 * `never` + `workspace-write` SEM rede (`politicaDoModo` do daemon). Nada
 * pergunta, nenhum revisor roda, e o sandbox continua sendo a trava de
 * terceiros. Medido antes do código (M0-25). Parâmetro OBRIGATÓRIO pela razão
 * de sempre: com um padrão, um chamador esquecido abriria (ou fecharia) a porta
 * calado. Desligada, tudo é exatamente como antes.
 */
export function modoDisponivelNaMaquina(
  modo: IdModoPermissao,
  motor: Motor,
  papelDaMaquina: PapelDaMaquina,
  bypassTotal: boolean,
): boolean {
  if (papelDaMaquina !== 'VPS') return true;
  switch (motor) {
    case 'CLAUDE':
      // O Claude na VPS continua com o `bypassPermissions` da D50: lá as
      // managed settings e o hook `PreToolUse` seguem valendo.
      return true;
    case 'CODEX':
      return modo !== 'bypassPermissions' || bypassTotal;
    case 'OPENCODE':
      /*
        Sessão comum OPENCODE nem existe na VPS (`motorDisponivelNaMaquina`),
        e a mestre nasce pelo `modoInicialDaMestre`, que não passa por aqui.
        Recusar bypass mesmo assim é a segunda barreira (Interface congelada
        item 2): no OpenCode `bypassPermissions` é "tudo liberado" SEM sandbox
        por baixo (OC-9), e se a primeira falhar esta ainda segura. A chave
        `BYPASS_TOTAL` NÃO abre isto: o que ela abre na VPS é um bypass com
        sandbox, e o OpenCode não tem sandbox para oferecer.
      */
      return modo !== 'bypassPermissions';
    default:
      return motorNaoTratado(motor);
  }
}

/**
 * O gate que a regra da casa prometia e nunca existiu em código: modo
 * perigoso exige ADMIN **e** confirmação explícita — na criação e na troca ao
 * vivo. Uma função, chamada pelas duas rotas, testada uma vez.
 */
export function podeUsarModo(
  papel: string,
  modo: IdModoPermissao,
  confirmado: boolean,
  motor: Motor,
  papelDaMaquina: PapelDaMaquina,
  bypassTotal: boolean,
):
  | { ok: true }
  | {
      ok: false;
      codigo:
        | 'sem_permissao'
        | 'modo_perigoso_sem_confirmacao'
        | 'modo_indisponivel_no_motor'
        | 'modo_indisponivel_na_vps';
    } {
  /*
    Existência ANTES de permissão, e a ordem é o comportamento: `plan` não é
    perigoso, então com a checagem depois um `('ADMIN','plan',true,'CODEX')`
    sairia pelo `return {ok:true}` da primeira linha — aprovado, e a sessão
    nasceria num modo que aquele CLI não tem.

    `motor` é parâmetro OBRIGATÓRIO pela mesma razão do `nivelDePergunta`: com
    `.default('CLAUDE')`, um chamador que esquecesse de passar liberaria `plan`
    no Codex e o compilador ficaria calado. Obrigatório, a violação não compila.
  */
  if (!modoDisponivelNoMotor(modo, motor)) {
    return { ok: false, codigo: 'modo_indisponivel_no_motor' };
  }
  /*
    A MÁQUINA vem antes do papel de quem pede, e a ordem é o comportamento:
    `bypassPermissions` é perigoso, então com a checagem depois um ADMIN que
    confirmasse sairia pelo `{ok:true}` — e a sessão nasceria sem sandbox
    nenhum na máquina que roda produção de terceiros. ADMIN pode escolher um
    modo perigoso; ninguém pode desligar a única barreira do Codex na VPS.
  */
  if (!modoDisponivelNaMaquina(modo, motor, papelDaMaquina, bypassTotal)) {
    return { ok: false, codigo: 'modo_indisponivel_na_vps' };
  }
  // A chave abre a MÁQUINA, nunca o papel: o bypass do Codex na VPS continua
  // perigoso, e passa pelo mesmo ADMIN + confirmação de todo bypass.
  if (!modoPerigoso(modo)) return { ok: true };
  if (papel !== 'ADMIN') return { ok: false, codigo: 'sem_permissao' };
  if (!confirmado) return { ok: false, codigo: 'modo_perigoso_sem_confirmacao' };
  return { ok: true };
}
